import { type ReactNode } from "react";
import { useIdentity } from "../identity/useIdentity";
import { useProject } from "./ProjectContext";

interface ProjectGateProps {
    children: ReactNode;
}

/**
 * Gates the main content area on a project being selected.
 *
 * Branches:
 *  - no-jwt → render children (auth modal handles it via parent gate).
 *  - loading → render children (parent IdentityGate already handles loading).
 *  - no-access → "no projects" banner; protected actions blocked.
 *  - needs-pick → multi-project picker; protected actions blocked.
 *  - ready → render children.
 */
export function ProjectGate({ children }: ProjectGateProps) {
    const { accessState, authorizedProjects, selectProject } = useProject();
    const { identity } = useIdentity();

    if (accessState === "no-jwt" || accessState === "loading" || accessState === "ready") {
        return <>{children}</>;
    }

    if (accessState === "no-access") {
        const orgName = identity?.organization.name ?? "this organization";
        return (
            <div className="callout callout-warn" data-testid="no-projects-banner" role="alert">
                <i className="fa-solid fa-circle-info" aria-hidden="true" />
                <div>
                    <div style={{ fontWeight: 600, marginBottom: 4 }}>
                        You don't have access to any projects in {orgName}.
                    </div>
                    <div>Contact your patient-registry admin.</div>
                </div>
            </div>
        );
    }

    // needs-pick
    return (
        <div className="project-picker" data-testid="project-picker">
            <div className="page-header">
                <div>
                    <div className="page-title">Select a project</div>
                    <div className="page-help">
                        You have access to multiple projects. Pick one to continue.
                    </div>
                </div>
            </div>
            <div className="picker-grid">
                {authorizedProjects.map((p) => (
                    <button
                        key={p.id}
                        type="button"
                        className="picker-card"
                        onClick={() => selectProject(p.id)}
                        data-testid={`project-pick-${p.id}`}
                    >
                        <i className="fa-solid fa-folder-tree" aria-hidden="true" />
                        <span className="picker-name">{p.name}</span>
                        <i className="fa-solid fa-arrow-right picker-arrow" aria-hidden="true" />
                    </button>
                ))}
            </div>
        </div>
    );
}
